import {
  Box,
  Button,
  Divider,
  Link,
  Menu,
  MenuItem,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { AgentEditContext } from 'components/@home/@settings/Tabs/Agents/AgentEditContext';
import { knowledgeCreateError } from 'components/@home/@settings/Tabs/Agents/CreateKnowledgeDialog';
import IdentityCard from 'components/@home/@settings/Tabs/Agents/IdentityCard';
import InstructionEditor from 'components/@home/@settings/Tabs/Agents/InstructionEditor';
import InstructionSaveFooter from 'components/@home/@settings/Tabs/Agents/InstructionSaveFooter';
import {
  detachKnowledge,
  isKnowledgeAttached,
  sanitizeKnowledgeAttachmentsForApi,
} from 'components/@home/@settings/Tabs/Agents/attachedKnowledgeHelpers';
import { sanitizeAttachmentsForApi } from 'components/@home/@settings/Tabs/Agents/attachedToolsHelpers';
import GeneralPanel from 'components/@home/@settings/Tabs/Agents/toolPanels/GeneralPanel';
import KnowledgeFolderSelector from 'components/@home/@settings/Tabs/Agents/KnowledgeFolderSelector';
import OmmnioDataGrid from 'components/common/OmmnioDataGrid';
import StatusChip from 'components/common/StatusChip';
import DatabaseOutlineIcon from 'mdi-react/DatabaseOutlineIcon';
import FileDocumentOutlineIcon from 'mdi-react/FileDocumentOutlineIcon';
import CogOutlineIcon from 'mdi-react/CogOutlineIcon';
import MessageTextOutlineIcon from 'mdi-react/MessageTextOutlineIcon';
import DotsHorizontalIcon from 'mdi-react/DotsHorizontalIcon';
import MinusIcon from 'mdi-react/MinusIcon';
import OpenInNewIcon from 'mdi-react/OpenInNewIcon';
import TrashCanOutlineIcon from 'mdi-react/TrashCanOutlineIcon';
import { bool, string } from 'prop-types';
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { buildAgenticPath } from 'utils/agenticRoute';
import KnowledgeApi from 'services/api/Knowledge';
import AgentSkill from 'services/api/AgentSkill';
import { normalizeToolTrigger } from 'constants/agenticToolTriggers';
import { attachedSkillsForAgent } from 'utils/skillToolsUnion';
import { I18n } from 'utils/i18n';

const getStyles = theme => ({
  root: {
    flex: '1 1 0',
    minHeight: 0,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    backgroundColor: theme.palette.background.paper,
  },
  chrome: {
    flexShrink: 0,
    padding: theme.spacing(2, 3, 0),
  },
  tabsRoot: {
    minHeight: 44,
    borderBottom: `1px solid ${theme.palette.divider}`,
    '& .MuiTabs-flexContainer': { gap: theme.spacing(2) },
  },
  tabRoot: {
    textTransform: 'none',
    minHeight: 44,
    minWidth: 'auto',
    padding: theme.spacing(1, 0.5),
    fontSize: theme.typography.pxToRem(13),
    fontWeight: 500,
  },
  tabContent: {
    flex: '1 1 0',
    minHeight: 0,
    overflowY: 'auto',
    padding: theme.spacing(2, 3, 3),
  },
  footer: {
    flexShrink: 0,
    borderTop: `1px solid ${theme.palette.divider}`,
    padding: theme.spacing(2, 3),
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: theme.spacing(1),
  },
  testResult: {
    color: theme.palette.text.primary,
    marginTop: theme.spacing(2),
    padding: theme.spacing(2),
    borderRadius: '8px',
    border: `1px solid ${theme.palette.divider}`,
    backgroundColor: theme.palette.background.default,
    whiteSpace: 'pre-wrap',
    fontSize: theme.typography.pxToRem(13),
    lineHeight: 1.5,
  },
});

const TAB_INSTRUCTIONS = 'instructions';
const TAB_CONFIGURATION = 'configuration';
const TAB_DOCUMENTS = 'documents';
const TAB_TEST = 'test';

const indexStatusLabel = status => {
  const key = {
    available: 'IndexStatusAvailable',
    processing: 'IndexStatusProcessing',
    failed: 'IndexStatusFailed',
    pending: 'IndexStatusPending',
  }[status || 'pending'];
  return I18n.t(`AgenticChatFlow.${key}`);
};

const indexStatusColor = status => {
  if (status === 'available') return 'success';
  if (status === 'processing') return 'info';
  if (status === 'failed') return 'error';
  return 'default';
};

const indexStatusChipProps = (status, row) => {
  const base = {
    label: indexStatusLabel(status),
    color: indexStatusColor(status),
  };
  if (status === 'processing') {
    return { ...base, loading: true, dot: false };
  }
  if (status === 'failed' && row.index?.errorMessage) {
    return { ...base, tooltip: row.index.errorMessage };
  }
  return base;
};

const normalizeKnowledge = fetched => ({
  ...fetched,
  trigger: normalizeToolTrigger(fetched.trigger || 'agent'),
  announcement: {
    whenToAnnounce: 'never',
    ...(fetched.announcement || {}),
  },
  longActionNotice: {
    enabled: false,
    waitSeconds: 0,
    ...(fetched.longActionNotice || {}),
  },
});

const serializeConfig = k =>
  JSON.stringify({
    trigger: k.trigger,
    announcement: k.announcement,
    longActionNotice: k.longActionNotice,
    forbidCancellation: k.forbidCancellation,
    forbidUserInterrupt: k.forbidUserInterrupt,
  });

const sharedDocsFolderUrl = folderId => {
  if (!folderId || typeof window === 'undefined') return null;
  return `${window.location.origin}/home/documents/shared/${folderId}/`;
};

const KnowledgeEditor = ({ knowledgeId: knowledgeIdProp = null, embedded = false }) => {
  const theme = useTheme();
  const styles = useMemo(() => getStyles(theme), [theme]);
  const navigate = useNavigate();
  const { id: routeId } = useParams();
  const id = knowledgeIdProp || routeId;
  const agentEdit = useContext(AgentEditContext);
  const { flow, authCompany } = agentEdit || {};
  const [knowledge, setKnowledge] = useState(null);
  const [identityAnchor, setIdentityAnchor] = useState(null);
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [draftDisplayName, setDraftDisplayName] = useState('');
  const [draftDescription, setDraftDescription] = useState('');
  const [savingIdentity, setSavingIdentity] = useState(false);
  const [folderOptions, setFolderOptions] = useState([]);
  const [draftFolderId, setDraftFolderId] = useState('');
  const [savingFolder, setSavingFolder] = useState(false);
  const [tab, setTab] = useState(TAB_INSTRUCTIONS);
  const [savedConfig, setSavedConfig] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saveFeedback, setSaveFeedback] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [folderId, setFolderId] = useState(null);
  const [docsLoading, setDocsLoading] = useState(false);
  const [reindexing, setReindexing] = useState(false);
  const [testQuery, setTestQuery] = useState('');
  const [testLoading, setTestLoading] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [instructionDirty, setInstructionDirty] = useState(false);
  const [instructionSaveFeedback, setInstructionSaveFeedback] = useState(null);
  const instructionEditorRef = useRef(null);

  const dirty = useMemo(() => {
    if (!knowledge || savedConfig == null) return false;
    return serializeConfig(knowledge) !== savedConfig;
  }, [knowledge, savedConfig]);

  const attachedSkills = useMemo(
    () => attachedSkillsForAgent(agentEdit?.agent, agentEdit?.skills || []),
    [agentEdit?.agent, agentEdit?.skills],
  );

  const skillsWithKnowledge = useMemo(
    () => attachedSkills.filter(skill => isKnowledgeAttached(skill.attachedKnowledge, id)),
    [attachedSkills, id],
  );

  const canDetach = Boolean(embedded && agentEdit?.onSaveAgent && skillsWithKnowledge.length > 0);

  useEffect(() => {
    if (!id) return;
    Promise.all([KnowledgeApi.get(id), KnowledgeApi.listFolderOptions(id)]).then(
      ([fetched, folders]) => {
        const normalized = normalizeKnowledge(fetched);
        setKnowledge(normalized);
        setSavedConfig(serializeConfig(normalized));
        setFolderOptions(folders || []);
        const fid = normalized.folderId || null;
        setFolderId(fid);
        setDraftFolderId(fid ? String(fid) : '');
      },
    );
  }, [id]);

  const loadDocuments = useCallback(
    (opts = {}) => {
      if (!id) return Promise.resolve();
      const { silent = false } = opts;
      if (!silent) setDocsLoading(true);
      return KnowledgeApi.listDocuments(id)
        .then(data => {
          setDocuments(data.documents || []);
          const fid = data.folderId || null;
          setFolderId(fid);
          setDraftFolderId(fid ? String(fid) : '');
        })
        .finally(() => {
          if (!silent) setDocsLoading(false);
        });
    },
    [id],
  );

  useEffect(() => {
    if (tab !== TAB_DOCUMENTS || !id) return;
    loadDocuments();
  }, [tab, id, loadDocuments]);

  useEffect(() => {
    if (tab !== TAB_DOCUMENTS || !id) return undefined;
    const needsPoll = documents.some(d =>
      ['pending', 'processing'].includes(d.index?.status || 'pending'),
    );
    if (!needsPoll) return undefined;
    const timer = setInterval(() => loadDocuments({ silent: true }), 5000);
    return () => clearInterval(timer);
  }, [tab, id, documents, loadDocuments]);

  const handleReindex = async () => {
    setReindexing(true);
    try {
      await KnowledgeApi.reindex(id);
      setSaveFeedback({
        severity: 'success',
        message: I18n.t('AgenticChatFlow.KnowledgeReindexStarted'),
      });
      await loadDocuments({ silent: true });
    } finally {
      setReindexing(false);
    }
  };

  const handleIdentityOpen = event => {
    setDraftDisplayName(knowledge.displayName || '');
    setDraftDescription(knowledge.description || '');
    setIdentityAnchor(event.currentTarget);
  };

  const closeIdentityPopover = () => {
    if (!savingIdentity) setIdentityAnchor(null);
  };

  const saveIdentity = async () => {
    setSavingIdentity(true);
    try {
      const updated = await KnowledgeApi.update(id, {
        displayName: draftDisplayName,
        description: draftDescription,
      });
      const normalized = normalizeKnowledge(updated);
      setKnowledge(normalized);
      setIdentityAnchor(null);
      agentEdit?.onReloadKnowledge?.();
    } finally {
      setSavingIdentity(false);
    }
  };

  const handleDelete = async () => {
    setMenuAnchor(null);
    await KnowledgeApi.destroy(id);
    agentEdit?.onReloadKnowledge?.();
    if (flow?._id && authCompany) {
      navigate(buildAgenticPath(authCompany, { flowId: flow._id, section: 'knowledge' }));
    }
  };

  const handleDetach = async () => {
    setMenuAnchor(null);
    if (!canDetach || !id) return;
    await Promise.all(
      skillsWithKnowledge.map(skill =>
        AgentSkill.update(skill._id, {
          displayName: skill.displayName,
          description: skill.description,
          attachedTools: sanitizeAttachmentsForApi(skill.attachedTools || []),
          attachedKnowledge: sanitizeKnowledgeAttachmentsForApi(
            detachKnowledge(skill.attachedKnowledge || [], id),
          ),
        }),
      ),
    );
    await agentEdit.onReloadSkills?.();
    if (flow?._id && authCompany) {
      navigate(buildAgenticPath(authCompany, { flowId: flow._id, section: 'knowledge' }));
    }
  };

  const folderDirty =
    draftFolderId && String(draftFolderId) !== String(knowledge?.folderId || folderId || '');

  const handleSaveFolder = async () => {
    if (!folderDirty) return;
    setSavingFolder(true);
    try {
      const updated = await KnowledgeApi.update(id, { folderId: draftFolderId });
      const normalized = normalizeKnowledge(updated);
      setKnowledge(normalized);
      setFolderId(normalized.folderId);
      setSaveFeedback({
        severity: 'success',
        message: I18n.t('AgenticChatFlow.KnowledgeFolderSaved'),
      });
      agentEdit?.onReloadKnowledge?.();
      await loadDocuments({ silent: true });
    } catch (err) {
      setSaveFeedback({
        severity: 'error',
        message: knowledgeCreateError(err),
      });
    } finally {
      setSavingFolder(false);
    }
  };

  const handleSaveConfig = async () => {
    if (!knowledge) return;
    setSaving(true);
    try {
      const updated = await KnowledgeApi.update(id, {
        trigger: knowledge.trigger,
        announcement: knowledge.announcement,
        longActionNotice: knowledge.longActionNotice,
        forbidCancellation: knowledge.forbidCancellation,
        forbidUserInterrupt: knowledge.forbidUserInterrupt,
      });
      const normalized = normalizeKnowledge(updated);
      setKnowledge(normalized);
      setSavedConfig(serializeConfig(normalized));
      setSaveFeedback({ severity: 'success', message: I18n.t('AgenticChatFlow.ToolSaved') });
      agentEdit?.onReloadKnowledge?.();
    } finally {
      setSaving(false);
    }
  };

  const handleTestQuery = async () => {
    const q = testQuery.trim();
    if (!q) return;
    setTestLoading(true);
    setTestResult(null);
    try {
      const result = await KnowledgeApi.query(id, q);
      setTestResult(result);
    } catch (err) {
      setTestResult({ error: err?.message || String(err) });
    } finally {
      setTestLoading(false);
    }
  };

  const handleInstructionFooterSave = useCallback(() => {
    instructionEditorRef.current?.openSaveDialog?.();
  }, []);

  const docColumns = useMemo(
    () => [
      {
        field: 'name',
        headerName: I18n.t('AgenticChatFlow.Name'),
        flex: 1.2,
        minWidth: 180,
        renderCell: ({ row }) => (
          <Typography
            variant="body2"
            noWrap
            title={row.name}
            sx={{
              fontWeight: 500,
              maxWidth: '100%',
            }}
          >
            {row.name}
          </Typography>
        ),
      },
      {
        field: 'parentFolder',
        headerName: I18n.t('AgenticChatFlow.KnowledgeDocumentFolder'),
        flex: 1,
        minWidth: 160,
        sortable: false,
        valueGetter: (value, row) => row.parentFolder?.label || row.parentFolder?.name || '',
        renderCell: ({ row }) => {
          const folder = row.parentFolder;
          if (!folder?._id) {
            return (
              <Typography
                variant="body2"
                sx={{
                  color: 'text.secondary',
                }}
              >
                —
              </Typography>
            );
          }
          const href = sharedDocsFolderUrl(folder._id);
          const label = folder.label || folder.name;
          return (
            <Link
              href={href}
              target="_blank"
              rel="noopener noreferrer"
              underline="hover"
              color="inherit"
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 0.5,
                minWidth: 0,
                maxWidth: '100%',
                fontSize: t => t.typography.pxToRem(13),
                color: 'text.secondary',
                '&:hover': { color: 'text.primary' },
                '& svg': { color: 'inherit' },
              }}
              onClick={e => e.stopPropagation()}
            >
              <Box
                component="span"
                title={label}
                sx={{
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  minWidth: 0,
                }}
              >
                {label}
              </Box>
              <OpenInNewIcon size={14} style={{ flexShrink: 0 }} />
            </Link>
          );
        },
      },
      {
        field: 'status',
        headerName: I18n.t('AgenticChatFlow.IndexStatus'),
        flex: 0.75,
        minWidth: 120,
        sortable: false,
        valueGetter: (value, row) => row.index?.status || 'pending',
        renderCell: ({ row }) => {
          const status = row.index?.status || 'pending';
          return <StatusChip {...indexStatusChipProps(status, row)} />;
        },
      },
      {
        field: 'chunks',
        headerName: I18n.t('AgenticChatFlow.Chunks'),
        flex: 0.5,
        minWidth: 80,
        align: 'right',
        headerAlign: 'right',
        valueGetter: (value, row) => row.index?.chunkCount ?? 0,
        renderCell: ({ value }) => (
          <Typography
            variant="body2"
            sx={{
              color: 'text.secondary',
              width: '100%',
              textAlign: 'right',
            }}
          >
            {value ?? 0}
          </Typography>
        ),
      },
    ],
    [],
  );

  if (!knowledge) return null;

  const folderUrl = sharedDocsFolderUrl(folderId || knowledge.folderId);

  return (
    <Box sx={[styles.root, embedded && { height: '100%' }]}>
      <Box sx={styles.chrome}>
        <IdentityCard
          icon={<DatabaseOutlineIcon size={18} />}
          title={knowledge.displayName || knowledge.name}
          description={knowledge.description}
          slug={knowledge.name}
          edit={{
            open: Boolean(identityAnchor),
            anchorEl: identityAnchor,
            onOpen: handleIdentityOpen,
            onClose: closeIdentityPopover,
            content: (
              <>
                <TextField
                  value={draftDisplayName}
                  onChange={e => setDraftDisplayName(e.target.value)}
                  size="small"
                  fullWidth
                  autoFocus
                  label={I18n.t('AgenticChatFlow.DisplayName')}
                />
                <TextField
                  value={draftDescription}
                  onChange={e => setDraftDescription(e.target.value)}
                  size="small"
                  fullWidth
                  multiline
                  minRows={3}
                  label={I18n.t('AgenticChatFlow.Description')}
                />
                <Stack
                  direction="row"
                  spacing={1}
                  sx={{
                    justifyContent: 'flex-end',
                  }}
                >
                  <Button onClick={closeIdentityPopover} disabled={savingIdentity} color="inherit">
                    {I18n.t('AgenticChatFlow.Cancel')}
                  </Button>
                  <Button
                    onClick={saveIdentity}
                    variant="contained"
                    color="primary"
                    disableElevation
                    disabled={savingIdentity}
                  >
                    {I18n.t('AgenticChatFlow.Save')}
                  </Button>
                </Stack>
              </>
            ),
          }}
          actions={
            <Button
              variant="outlined"
              color="secondary"
              onClick={e => setMenuAnchor(e.currentTarget)}
              size="small"
            >
              <DotsHorizontalIcon size={20} />
            </Button>
          }
        />
        <Menu
          anchorEl={menuAnchor}
          open={Boolean(menuAnchor)}
          onClose={() => setMenuAnchor(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
          {canDetach && (
            <MenuItem onClick={handleDetach}>
              <MinusIcon size={16} style={{ marginRight: 8 }} />
              {I18n.t('AgenticChatFlow.Detach')}
            </MenuItem>
          )}
          {canDetach && <Divider />}
          <MenuItem onClick={handleDelete} sx={{ color: 'error.main' }}>
            <TrashCanOutlineIcon size={16} style={{ marginRight: 8 }} />
            {I18n.t('AgenticChatFlow.Delete')}
          </MenuItem>
        </Menu>
        <Tabs
          value={tab}
          onChange={(_, v) => setTab(v)}
          sx={styles.tabsRoot}
          TabIndicatorProps={{ sx: { height: 2 } }}
        >
          <Tab
            sx={styles.tabRoot}
            value={TAB_INSTRUCTIONS}
            icon={<FileDocumentOutlineIcon size={16} />}
            iconPosition="start"
            label={I18n.t('AgenticChatFlow.TabInstructions')}
          />
          <Tab
            sx={styles.tabRoot}
            value={TAB_CONFIGURATION}
            icon={<CogOutlineIcon size={16} />}
            iconPosition="start"
            label={I18n.t('AgenticChatFlow.TabConfiguration')}
          />
          <Tab
            sx={styles.tabRoot}
            value={TAB_DOCUMENTS}
            icon={<DatabaseOutlineIcon size={16} />}
            iconPosition="start"
            label={I18n.t('AgenticChatFlow.TabDocuments')}
          />
          <Tab
            sx={styles.tabRoot}
            value={TAB_TEST}
            icon={<MessageTextOutlineIcon size={16} />}
            iconPosition="start"
            label={I18n.t('AgenticChatFlow.TabTest')}
          />
        </Tabs>
      </Box>

      <Box sx={styles.tabContent}>
        {tab === TAB_INSTRUCTIONS && (
          <InstructionEditor
            ref={instructionEditorRef}
            entityType="knowledge"
            entityId={knowledge._id}
            title={I18n.t('AgenticChatFlow.KnowledgeInstructionTitle')}
            parentManagedSave
            onDirtyChange={setInstructionDirty}
            onSaved={() => {
              setInstructionSaveFeedback({
                severity: 'success',
                message: I18n.t('AgenticChatFlow.ToolSaved'),
              });
            }}
          />
        )}

        {tab === TAB_CONFIGURATION && <GeneralPanel tool={knowledge} setTool={setKnowledge} />}

        {tab === TAB_DOCUMENTS && (
          <Stack spacing={2}>
            <Typography
              variant="body2"
              sx={{
                color: 'text.secondary',
              }}
            >
              {I18n.t('AgenticChatFlow.KnowledgeDocumentsHelper')}
            </Typography>
            <KnowledgeFolderSelector
              folderId={draftFolderId}
              onFolderIdChange={setDraftFolderId}
              selectableFolders={folderOptions}
              showSave={folderDirty}
              onSave={handleSaveFolder}
              saving={savingFolder}
            />
            <Stack
              direction="row"
              spacing={1}
              useFlexGap
              sx={{
                flexWrap: 'wrap',
              }}
            >
              {folderUrl && (
                <Button
                  component={Link}
                  href={folderUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  variant="outlined"
                  color="secondary"
                  size="small"
                  startIcon={<OpenInNewIcon size={16} />}
                >
                  {I18n.t('AgenticChatFlow.OpenSharedFolder')}
                </Button>
              )}
              <Button
                variant="outlined"
                color="secondary"
                size="small"
                onClick={handleReindex}
                disabled={reindexing || docsLoading}
              >
                {I18n.t('AgenticChatFlow.ReindexDocuments')}
              </Button>
            </Stack>
            <OmmnioDataGrid
              rows={documents}
              columns={docColumns}
              loading={docsLoading}
              getRowId={r => r._id}
              paginationMode="client"
              pageSize={Math.max(documents.length, 1)}
              hideFooter={documents.length <= 25}
              autoHeight
              disableEvenOdd
              sx={{ minHeight: documents.length ? undefined : 120 }}
            />
          </Stack>
        )}

        {tab === TAB_TEST && (
          <Stack spacing={2} sx={{ maxWidth: 720 }}>
            <Typography
              variant="body2"
              sx={{
                color: 'text.secondary',
              }}
            >
              {I18n.t('AgenticChatFlow.KnowledgeTestHelper')}
            </Typography>
            <TextField
              label={I18n.t('AgenticChatFlow.KnowledgeTestQuery')}
              value={testQuery}
              onChange={e => setTestQuery(e.target.value)}
              multiline
              minRows={2}
              fullWidth
              size="small"
            />
            <Button
              variant="contained"
              onClick={handleTestQuery}
              disabled={testLoading || !testQuery.trim()}
            >
              {I18n.t('AgenticChatFlow.RunQuery')}
            </Button>
            {testResult && (
              <Box sx={styles.testResult}>
                {testResult.error
                  ? testResult.error
                  : testResult.answer || JSON.stringify(testResult, null, 2)}
              </Box>
            )}
          </Stack>
        )}
      </Box>

      {tab === TAB_INSTRUCTIONS && (
        <InstructionSaveFooter
          dirty={instructionDirty}
          saveFeedback={instructionSaveFeedback}
          onSave={handleInstructionFooterSave}
        />
      )}

      {tab === TAB_CONFIGURATION && (
        <Box sx={styles.footer}>
          {saveFeedback?.message && (
            <Typography
              sx={{
                mr: 'auto',
                fontSize: 13,
                fontWeight: 500,
                color: saveFeedback.severity === 'success' ? 'success.main' : 'error.main',
              }}
            >
              {saveFeedback.message}
            </Typography>
          )}
          <Button onClick={handleSaveConfig} variant="contained" disabled={saving || !dirty}>
            {I18n.t('AgenticChatFlow.Save')}
          </Button>
        </Box>
      )}
    </Box>
  );
};

KnowledgeEditor.propTypes = {
  knowledgeId: string,
  embedded: bool,
};

export default KnowledgeEditor;
